import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  GET_LABOR_MEASUREMENTS_FOR_PROJECT,
  GET_MATERIAL_MEASUREMENTS_FOR_PROJECT,
  GET_PROJECT_PRODUCTS,
} from "../../../../apolloClient/operation/querys/userQuery";
import {
  MaterialsForProjectResponse,
  MaterialsMeasurementDataResponse,
  ProjectResponse,
} from "../../../../types/graphQlQuery.interface";
import { Product, Project } from "../../../../redux/types";
import {
  CustomerProjectDetails,
  ProjectMeasurementInput,
} from "../../../../redux/types/projectDetails";
import { AppCSS } from "../../../../components";
import { CREATE_PROJECT_MATERIAL_MEASUREMENT } from "../../../../apolloClient/operation/mutations/mutation";
import { toast } from "react-toastify";
import AppLoader from "../../../../components/loader";
import { useFetchMaterialMeasurements } from "../../../../apolloClient/hooks/itemCreation/fetchHooks";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";

interface MaterialProps {
  project: CustomerProjectDetails;
  refetchData: Function;
  permissions?: boolean;
}

export default function Materials({
  project,
  refetchData,
  permissions,
}: MaterialProps) {
  const [measurements, setMeasurements] = useState<ProjectMeasurementInput[]>(
    []
  );
  const [isEditing, setIsEditing] = useState(false);
  const [originalMeasurements, setOriginalMeasurements] = useState<ProjectMeasurementInput[]>([]);
  const [expandedAccordions, setExpandedAccordions] = useState<{ [key: number]: boolean }>({});

  const {
    data: materialData,
    loading: materialLoading,
    refetch: refetchMeasurements,
  } = useQuery<MaterialsMeasurementDataResponse>(
    GET_MATERIAL_MEASUREMENTS_FOR_PROJECT,
    {
      variables: { limit: 1000, offset: 0 },
      fetchPolicy: "no-cache",
    }
  );

  // const { loading, error, materialMeasurementsData } =
  //   useFetchMaterialMeasurements({
  //     limit: 100,
  //     offset: 0,
  //   });

  // console.log("the material data", materialData);

  const [addProjectMaterialMeasurement] = useMutation<
    {
      addProjectMaterialMeasurement: boolean;
    },
    { input: ProjectMeasurementInput[] }
  >(CREATE_PROJECT_MATERIAL_MEASUREMENT);

  const {
    data: productsData,
    loading: productsLoading,
    refetch,
  } = useQuery<ProjectResponse>(GET_PROJECT_PRODUCTS, {
    variables: { id: project.projectId },
    fetchPolicy: "no-cache",
  });


  // Re-fetch data whenever this tab is visited (component mounts)
  useEffect(() => {
    refetchData();
    refetchMeasurements();
  }, []);

  useEffect(() => {
    if (project && materialData) {
      let endMeasurements: any[] = [];
      const allMeasurements = materialData?.materialMeasurements || [];
      const addedMeasurements =
        project?.insuranceScope?.materialMeasurements?.map((measurement) =>
          measurement?.measurements?.map((endMeasure) => {
            console.log("listed measurement", {
              projectId: project?.projectId,
              measurementId: endMeasure?.id,
              quantity: endMeasure?.value?.quantity,
              productId: measurement?.productId,
              productName: measurement?.productId,
              name: endMeasure?.name,
              unit: endMeasure?.unit,
            });
            let found = endMeasurements.some(
              (measure) =>
                endMeasure?.id === measure.measurementId &&
                measurement?.productId === measure.productId
            );
            if (found) {
              return;
            } else {
              endMeasurements.push({
                projectId: project?.projectId,
                measurementId: endMeasure?.id,
                quantity: endMeasure?.value?.quantity ?? 0,
                productId: measurement?.productId,
                productName: measurement?.productId,
                name: endMeasure?.name,
                unit: endMeasure?.unit,
              });
            }
          })
        );
      console.log("the added", endMeasurements);
      // console.log("next update", materialData);
      allMeasurements?.forEach((measurement) => {
        // console.log("logged meas", measurement);
        measurement?.parents?.forEach((parent) => {
          let found = endMeasurements.some(
            (measure) =>
              measurement?.id === measure.measurementId &&
              parent?.id === measure.productId
          );
          // console.log("find", found);
          if (found) {
            return;
          } else {
            endMeasurements.push({
              projectId: project?.projectId,
              measurementId: measurement?.id,
              quantity: 0,
              productId: parent?.id,
              productName: parent?.id,
              name: measurement?.name,
              unit: measurement?.unit,
            });
          }
        });
      });
      setMeasurements(endMeasurements);
      setOriginalMeasurements(endMeasurements);
    }
  }, [project, materialData]);

  const createMeasurement = (measurement: ProjectMeasurementInput) => {
    return addProjectMaterialMeasurement({ variables: { input: [measurement] } });
  };

  const handleMeasurementChange = (
    measurementId: number,
    quantity: number,
    productId: number
  ) => {
    setMeasurements((prevMeasurements) =>
      prevMeasurements.map((m) =>
        m.measurementId === measurementId && m.productId === productId
          ? { ...m, quantity }
          : m
      )
    );
  };

  const handleAccordionChange = (productId: number) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [productId]: isExpanded
    }));
  };

  const handleEditClick = async () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      const changedInputs = measurements.filter((measurement) => {
        const originalMeasurement = originalMeasurements.find(
          (m) =>
            m.measurementId === measurement.measurementId &&
            m.productId === measurement.productId
        );
        return (
          originalMeasurement &&
          originalMeasurement.quantity !== measurement.quantity
        );
      });

      if (changedInputs.length === 0) {
        toast("No changes detected", {
          type: "info",
          position: "top-left",
          className: "toast-message",
        });
        setIsEditing(false);
        return;
      }

      try {
        await addProjectMaterialMeasurement({
          variables: { input: changedInputs.map((measurement) => ({
            projectId: project.projectId,
            measurementId: measurement.measurementId,
            quantity: measurement.quantity,
            productId: measurement.productId,
          })) },
        });
        toast("Measurements updated", { type: "success" });
        
        // Track material save
        logEvent(PROJECT_EVENTS.INSURANCE_MATERIAL_SAVED, {
          project_id: project.projectId,
          measurements_count: changedInputs.length,
          timestamp: new Date().toISOString(),
        });
        
        setOriginalMeasurements([...measurements]);
        setIsEditing(false);
      } catch (error) {
        toast("Error updating measurements", { type: "error" });
      }
    }
  };

  const handleCancelClick = () => {
    setMeasurements([...originalMeasurements]);
    setIsEditing(false);
  };

  if (productsLoading || materialLoading) {
    return <AppLoader isLoading={true} />;
  }

  const productsList = productsData?.project?.products || [];

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          justifyContent: "flex-end",
          mb: 3,
        }}
      >
        {permissions && (
          <>
            <Box
              component="button"
              onClick={handleEditClick}
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: "#4b87ed",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: "#3a6fd8",
                },
              }}
            >
              {isEditing ? "Save" : "Edit"}
            </Box>
            {isEditing && (
              <Box
                component="button"
                onClick={handleCancelClick}
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: "#5a6268",
                  },
                }}
              >
                Cancel
              </Box>
            )}
          </>
        )}
      </Box>
      <Box>
        {productsList.length > 0 ? (
          productsList.map((productSystem) => (
            <Accordion
              key={productSystem.id}
              expanded={expandedAccordions[productSystem.id] || false}
              onChange={handleAccordionChange(productSystem.id)}
              sx={{
                mb: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                borderRadius: "8px !important",
                border: "1px solid #e5e7eb",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "0 0 16px 0 !important",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#f8f9fa",
                  minHeight: "56px",
                  borderRadius: "8px",
                  "&.Mui-expanded": {
                    minHeight: "56px",
                    borderRadius: "8px 8px 0 0",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                    "&.Mui-expanded": {
                      margin: "12px 0",
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    fontWeight: 600,
                    color: "#1f2937",
                  }}
                >
                  {capitalizeFirstLetter(productSystem?.name)}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  padding: 0,
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "0 0 8px 8px",
                    overflow: "hidden",
                  }}
                >
                  {Array.isArray(measurements) && measurements
                    .filter((measurement) => measurement.productName === productSystem.id || measurement.productId === productSystem.id)
                    .map((measurement, index) => (
                      <Box
                        key={`${measurement.measurementId}-${productSystem.id}`}
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: { xs: 1, sm: 2 },
                          p: { xs: 2, sm: 2 },
                          borderBottom: index < measurements.filter(m => m.productName === productSystem.id || m.productId === productSystem.id).length - 1
                            ? "1px solid #e5e7eb"
                            : "none",
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#374151",
                            mb: { xs: 1, sm: 0 },
                          }}
                        >
                          {capitalizeFirstLetter(measurement?.name)}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          <input
                            type="number"
                            readOnly={!isEditing}
                            value={
                              measurements.find(
                                (m) =>
                                  m.measurementId === measurement.measurementId &&
                                  (m.productId === productSystem.id || m.productName === productSystem.id)
                              )?.quantity || ""
                            }
                            onChange={(e) => {
                              const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                              handleMeasurementChange(
                                measurement.measurementId,
                                isNaN(value) ? 0 : value,
                                productSystem.id
                              );
                            }}
                            style={{
                              width: "120px",
                              height: "40px",
                              padding: "0 12px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px 0 0 6px",
                              fontSize: "14px",
                              outline: "none",
                              backgroundColor: isEditing ? "#fff" : "#f9fafb",
                            }}
                          />
                          <Box
                            sx={{
                              width: "60px",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#374151",
                              color: "white",
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: "0 6px 6px 0",
                              border: "1px solid #374151",
                              borderLeft: "none",
                            }}
                          >
                            {(measurement?.unit || "").toUpperCase()}
                          </Box>
                        </Box>
                      </Box>
                    ))
                  }
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
              border: "1px dashed #d1d5db",
              color: "#6b7280",
              fontSize: 14,
            }}
          >
            No product systems found for this project.
          </Box>
        )}
      </Box>
    </Box>
  );
}

function capitalizeFirstLetter(str?: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}
